import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Download,
  Sparkles,
  CircleAlert as AlertCircle,
  CircleCheck as CheckCircle2,
  Loader as Loader2,
  Undo2,
  BadgeCheck,
  ShieldAlert,
  MessageSquareQuote,
  Wallet,
  X,
} from 'lucide-react';

interface ImportJob {
  id: string;
  status: string;
  days_back: number;
  chats_total: number;
  chats_done: number;
  next_chat_index: number;
  batch_size: number;
  messages_imported: number;
  conversations_upserted: number;
  matched_approved: number;
  matched_rejected: number;
  matched_uncontracted: number;
  last_error: string | null;
  started_at: string;
  finished_at: string | null;
}

interface LearningRun {
  id: string;
  status: string;
  cap_brl: number;
  estimated_cost_brl: number;
  actual_cost_brl: number;
  approved_analyzed: number;
  rejected_analyzed: number;
  informal_rejected_detected: number;
  classifier_conversations: number;
  extracted_questions: string[];
  extracted_champion_phrases: string[];
  extracted_objections: Array<{ objection: string; response: string }>;
  extracted_red_flags: string[];
  extracted_green_flags: string[];
  extracted_rejection_phrases: string[];
  summary: string | null;
  new_script_id: string | null;
  previous_script_id: string | null;
  last_error: string | null;
  started_at: string;
  finished_at: string | null;
}

interface Estimate {
  approved: number;
  rejected_formal: number;
  uncontracted: number;
  estimated_cost_brl: number;
}

const IMPORT_STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila',
  listing_chats: 'Buscando conversas no celular',
  importing: 'Importando mensagens',
  done: 'Concluido',
  failed: 'Falhou',
};

const RUN_STATUS_LABEL: Record<string, string> = {
  estimating: 'Iniciando',
  loading: 'Carregando conversas',
  classifying: 'Classificando recusas',
  extracting: 'Aprendendo padroes',
  done: 'Concluido',
  capped: 'Bloqueado pelo teto',
  failed: 'Falhou',
};

export default function LearningTab({ sellerId, unitId }: { sellerId: string; unitId: string | null }) {
  const { user } = useAuth();
  const [daysBack, setDaysBack] = useState<number>(7);
  const [importJob, setImportJob] = useState<ImportJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [runningLearn, setRunningLearn] = useState(false);
  const [learningRun, setLearningRun] = useState<LearningRun | null>(null);
  const [runs, setRuns] = useState<LearningRun[]>([]);
  const [reverting, setReverting] = useState(false);
  const capBrl = 20;

  useEffect(() => {
    void loadLatestImport();
    void loadLatestRun();
    void loadEstimate();
  }, [sellerId]);

  useEffect(() => {
    if (!importJob) return;
    if (importJob.status === 'done' || importJob.status === 'failed') return;
    const t = setInterval(() => void pollImport(importJob.id), 2500);
    return () => clearInterval(t);
  }, [importJob]);

  useEffect(() => {
    if (!learningRun) return;
    if (['done', 'failed', 'capped'].includes(learningRun.status)) return;
    const t = setInterval(() => void pollRun(learningRun.id), 3000);
    return () => clearInterval(t);
  }, [learningRun]);

  async function loadLatestImport() {
    const { data } = await supabase
      .from('whatsapp_history_imports')
      .select('*')
      .eq('seller_id', sellerId)
      .order('started_at', { ascending: false })
      .limit(1);
    setImportJob(data && data.length > 0 ? (data[0] as ImportJob) : null);
  }

  async function loadLatestRun() {
    const { data } = await supabase
      .from('ai_learning_runs')
      .select('*')
      .eq('seller_id', sellerId)
      .order('started_at', { ascending: false })
      .limit(10);
    const list = (data ?? []) as LearningRun[];
    setRuns(list);
    setLearningRun(list.length > 0 ? list[0] : null);
  }

  async function loadEstimate() {
    setEstimating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-learn-from-history`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'estimate', seller_id: sellerId }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json?.ok) {
        setEstimate({
          approved: Number(json.approved || 0),
          rejected_formal: Number(json.rejected_formal || 0),
          uncontracted: Number(json.uncontracted || 0),
          estimated_cost_brl: Number(json.estimated_cost_brl || 0),
        });
      }
    } finally {
      setEstimating(false);
    }
  }

  async function pollImport(id: string) {
    const { data } = await supabase
      .from('whatsapp_history_imports')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (data) {
      setImportJob(data as ImportJob);
      if ((data as any).status === 'done') void loadEstimate();
    }
  }

  async function pollRun(id: string) {
    const { data } = await supabase
      .from('ai_learning_runs')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (data) {
      setLearningRun(data as LearningRun);
      if (['done', 'failed', 'capped'].includes((data as any).status)) {
        void loadLatestRun();
      }
    }
  }

  async function startImport() {
    if (!user) return;
    setStarting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-history-import`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'start', seller_id: sellerId, days_back: daysBack }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Falha ao iniciar');
      await loadLatestImport();
    } catch (err) {
      alert('Nao foi possivel iniciar a importacao: ' + (err as Error).message);
    } finally {
      setStarting(false);
    }
  }

  async function cancelImport() {
    if (!importJob) return;
    if (!confirm('Cancelar a importacao em andamento? O progresso salvo ate agora sera mantido, mas o job atual sera encerrado.')) return;
    setCancelling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-history-import`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'cancel', seller_id: sellerId }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Falha ao cancelar');
      await loadLatestImport();
    } catch (err) {
      alert('Nao foi possivel cancelar: ' + (err as Error).message);
    } finally {
      setCancelling(false);
    }
  }

  async function resumeImport() {
    if (!importJob) return;
    setResuming(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-history-import`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'resume', import_id: importJob.id }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Falha ao continuar');
      await loadLatestImport();
    } catch (err) {
      alert('Nao foi possivel continuar: ' + (err as Error).message);
    } finally {
      setResuming(false);
    }
  }

  async function runLearn() {
    if (!user) return;
    if (!estimate) return;
    if (estimate.estimated_cost_brl > capBrl) {
      const ok = confirm(
        `A estimativa (R$ ${estimate.estimated_cost_brl.toFixed(2)}) passa do teto de R$ ${capBrl.toFixed(2)}. Deseja continuar mesmo assim?`,
      );
      if (!ok) return;
    }
    setRunningLearn(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-learn-from-history`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({
            action: 'run',
            seller_id: sellerId,
            unit_id: unitId,
            cap_brl: Math.max(capBrl, estimate.estimated_cost_brl + 1),
          }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Falha ao iniciar aprendizado');
      await loadLatestRun();
    } catch (err) {
      alert('Nao foi possivel iniciar o aprendizado: ' + (err as Error).message);
    } finally {
      setRunningLearn(false);
    }
  }

  async function revertScript() {
    if (!learningRun || !learningRun.previous_script_id) return;
    if (!confirm('Reverter para o roteiro anterior?')) return;
    setReverting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-learn-from-history`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'revert', run_id: learningRun.id }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Falha ao reverter');
      await loadLatestRun();
    } catch (err) {
      alert('Nao foi possivel reverter: ' + (err as Error).message);
    } finally {
      setReverting(false);
    }
  }

  const importProgress = useMemo(() => {
    if (!importJob) return 0;
    if (importJob.chats_total === 0) return 0;
    return Math.min(100, Math.round((importJob.chats_done / importJob.chats_total) * 100));
  }, [importJob]);

  const isImporting = importJob && ['queued', 'listing_chats', 'importing'].includes(importJob.status);
  const isLearning = learningRun && ['estimating', 'loading', 'classifying', 'extracting'].includes(learningRun.status);
  const learnedFromCurrent = learningRun && learningRun.status === 'done';

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6">
        <div className="mb-4 flex items-start gap-3">
          <div className="rounded-lg bg-emerald-100 p-2">
            <Download className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Passo 1 - Importar historico do WhatsApp da Lorena</h3>
            <p className="mt-1 text-sm text-slate-600">
              Traz as conversas antigas do celular dela para o sistema. Depois cruzamos com sua base de contratos.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Periodo</label>
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              disabled={!!isImporting}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <option value={7}>Ultimos 7 dias (recomendado)</option>
              <option value={15}>Ultimos 15 dias</option>
              <option value={30}>Ultimos 30 dias</option>
              <option value={60}>Ultimos 60 dias</option>
              <option value={90}>Ultimos 90 dias</option>
              <option value={180}>Ultimos 180 dias</option>
              <option value={365}>Ultimo ano</option>
            </select>
          </div>
          <button
            onClick={startImport}
            disabled={!!isImporting || starting}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {starting || isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isImporting ? 'Importando...' : 'Importar historico'}
          </button>
          {isImporting && (
            <button
              onClick={cancelImport}
              disabled={cancelling}
              className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              Cancelar
            </button>
          )}
        </div>

        {importJob && (
          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-800">{IMPORT_STATUS_LABEL[importJob.status] || importJob.status}</span>
              <span className="text-slate-500">
                {importJob.chats_done} / {importJob.chats_total || '-'} conversas
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${importProgress}%` }}
              />
            </div>
            {importJob.chats_total > 0 && importJob.batch_size > 0 && (
              <p className="text-xs text-slate-500">
                Processando bloco {Math.min(
                  Math.floor(importJob.chats_done / importJob.batch_size) + (isImporting ? 1 : 0),
                  Math.ceil(importJob.chats_total / importJob.batch_size),
                )} de {Math.ceil(importJob.chats_total / importJob.batch_size)} - {importJob.batch_size} conversas por bloco - {importJob.messages_imported} mensagens baixadas.
              </p>
            )}
            {importJob.status === 'done' && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatBadge icon={BadgeCheck} label="Aprovadas" value={importJob.matched_approved} color="emerald" />
                <StatBadge icon={ShieldAlert} label="Recusadas formais" value={importJob.matched_rejected} color="red" />
                <StatBadge icon={MessageSquareQuote} label="Sem contrato" value={importJob.matched_uncontracted} color="amber" />
                <StatBadge icon={Sparkles} label="Mensagens" value={importJob.messages_imported} color="slate" />
              </div>
            )}
            {importJob.last_error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{importJob.last_error}</span>
              </div>
            )}
            {importJob.status !== 'done' && !isImporting && importJob.chats_done < importJob.chats_total && (
              <button
                onClick={resumeImport}
                disabled={resuming}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50"
              >
                {resuming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                Continuar de onde parou
              </button>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Sparkles className="h-5 w-5 text-blue-700" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Passo 2 - Aprender com as conversas importadas</h3>
              <p className="mt-1 text-sm text-slate-600">
                A IA detecta recusas informais, extrai perguntas, frases campeas, objecoes e sinais de alerta. O roteiro e atualizado automaticamente.
              </p>
            </div>
          </div>
          <div className="hidden items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:flex">
            <Wallet className="h-4 w-4" />
            Teto: R$ {capBrl.toFixed(2)}
          </div>
        </div>

        {estimate && (
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatBadge icon={BadgeCheck} label="Aprovadas" value={estimate.approved} color="emerald" />
            <StatBadge icon={ShieldAlert} label="Recusadas" value={estimate.rejected_formal} color="red" />
            <StatBadge icon={MessageSquareQuote} label="Sem contrato" value={estimate.uncontracted} color="amber" />
            <StatBadge
              icon={Wallet}
              label="Custo estimado"
              value={`R$ ${estimate.estimated_cost_brl.toFixed(2)}`}
              color={estimate.estimated_cost_brl > capBrl ? 'red' : 'blue'}
            />
          </div>
        )}

        {estimate && estimate.estimated_cost_brl > capBrl && (
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>A estimativa passa do teto de R$ {capBrl.toFixed(2)}. Voce pode aumentar o teto ao rodar, ou importar um periodo menor.</span>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={runLearn}
            disabled={!!isLearning || runningLearn || !estimate || (estimate.approved === 0 && estimate.rejected_formal === 0 && estimate.uncontracted === 0)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {runningLearn || isLearning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {isLearning ? 'Aprendendo...' : 'Aprender do historico agora'}
          </button>
          <button
            onClick={loadEstimate}
            disabled={estimating}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            {estimating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Atualizar estimativa
          </button>
        </div>

        {learningRun && (
          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-800">{RUN_STATUS_LABEL[learningRun.status] || learningRun.status}</span>
              {learningRun.status === 'done' && (
                <span className="text-slate-500">Custo real: R$ {Number(learningRun.actual_cost_brl || 0).toFixed(2)}</span>
              )}
            </div>
            {isLearning && (
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full w-2/3 animate-pulse bg-blue-500" />
              </div>
            )}
            {learningRun.last_error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{learningRun.last_error}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {learnedFromCurrent && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-1 h-5 w-5 text-emerald-600" />
              <div>
                <h3 className="text-base font-semibold text-slate-900">O que a IA aprendeu</h3>
                {learningRun.summary && (
                  <p className="mt-1 text-sm text-slate-700">{learningRun.summary}</p>
                )}
              </div>
            </div>
            {learningRun.previous_script_id && (
              <button
                onClick={revertScript}
                disabled={reverting}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
              >
                {reverting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                Reverter roteiro
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <LearnCard title="Perguntas frequentes" items={learningRun.extracted_questions} color="emerald" />
            <LearnCard title="Frases campeas" items={learningRun.extracted_champion_phrases} color="emerald" />
            <LearnCard
              title="Objecoes com resposta"
              items={learningRun.extracted_objections.map((o) => `${o.objection} -> ${o.response}`)}
              color="blue"
            />
            <LearnCard title="Sinais positivos" items={learningRun.extracted_green_flags} color="emerald" />
            <LearnCard title="Sinais de alerta" items={learningRun.extracted_red_flags} color="red" />
            <LearnCard title="Formas educadas de recusar" items={learningRun.extracted_rejection_phrases} color="amber" />
          </div>
        </div>
      )}

      {runs.length > 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">Historico de aprendizados</h3>
          <div className="space-y-2">
            {runs.slice(1).map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                <div className="text-slate-700">
                  {new Date(r.started_at).toLocaleString('pt-BR')} - {RUN_STATUS_LABEL[r.status] || r.status}
                </div>
                <div className="text-slate-500">
                  {r.approved_analyzed} apr / {r.rejected_analyzed} rec - R$ {Number(r.actual_cost_brl || 0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBadge({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color: 'emerald' | 'red' | 'amber' | 'blue' | 'slate';
}) {
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    slate: 'bg-slate-50 text-slate-800 border-slate-200',
  };
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${colorMap[color]}`}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <div className="min-w-0">
        <div className="truncate text-[11px] uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}

function LearnCard({ title, items, color }: { title: string; items: string[]; color: 'emerald' | 'red' | 'amber' | 'blue' }) {
  const colorMap: Record<string, string> = {
    emerald: 'border-emerald-200 bg-white',
    red: 'border-red-200 bg-white',
    amber: 'border-amber-200 bg-white',
    blue: 'border-blue-200 bg-white',
  };
  const headMap: Record<string, string> = {
    emerald: 'text-emerald-800',
    red: 'text-red-800',
    amber: 'text-amber-800',
    blue: 'text-blue-800',
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <h4 className={`mb-2 text-xs font-semibold uppercase tracking-wide ${headMap[color]}`}>{title}</h4>
      {items.length === 0 ? (
        <p className="text-xs italic text-slate-400">Nada extraido nesta execucao.</p>
      ) : (
        <ul className="space-y-1 text-sm text-slate-700">
          {items.slice(0, 12).map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-slate-400" />
              <span className="leading-snug">{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
